'use client';

import { useSearchParams } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

const PROGRAM_LINK_COPY = {
  expired: {
    title: 'Offer ended',
    description: 'That offer has ended, but you can still browse plans.',
  },
  inactive: {
    title: 'Offer unavailable',
    description: 'That offer is no longer available. Browse our current plans.',
  },
} as const;

const DISMISS_STORAGE_KEY = 'motito_program_link_state_dismissed';

export function ProgramLinkStateStrip() {
  const searchParams = useSearchParams();
  const initialLinkState = searchParams?.get('program_link_state');
  const [dismissed, setDismissed] = useState(false);

  const linkState = useMemo(() => {
    if (initialLinkState === 'expired' || initialLinkState === 'inactive') {
      return initialLinkState;
    }
    return null;
  }, [initialLinkState]);

  useEffect(() => {
    if (typeof window === 'undefined' || !linkState) {
      return;
    }

    const storedState = window.sessionStorage.getItem(DISMISS_STORAGE_KEY);
    if (storedState === linkState || storedState === 'all') {
      setDismissed(true);
    }

    const url = new URL(window.location.href);
    url.searchParams.delete('program_link_state');
    const scrubbedUrl = `${url.pathname}${url.search}${url.hash}`;
    const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;

    if (scrubbedUrl !== currentUrl) {
      window.history.replaceState(window.history.state, '', scrubbedUrl);
    }
  }, [linkState]);

  if (!linkState || dismissed) {
    return null;
  }

  const copy = PROGRAM_LINK_COPY[linkState];

  return (
    <Alert
      className="mx-auto mb-4 mt-2 max-w-6xl border-slate-200 bg-slate-50 text-slate-800"
      data-testid="program-link-state-strip"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <AlertTitle className="text-sm font-semibold text-slate-900">
            {copy.title}
          </AlertTitle>
          <AlertDescription className="text-sm text-slate-700">
            <span>{copy.description}</span>
          </AlertDescription>
        </div>
        <button
          type="button"
          className="shrink-0 text-sm font-medium text-slate-600 underline-offset-2 hover:text-slate-900 hover:underline"
          data-testid="program-link-state-dismiss"
          onClick={() => {
            if (typeof window !== 'undefined') {
              window.sessionStorage.setItem(DISMISS_STORAGE_KEY, 'all');
            }
            setDismissed(true);
          }}
        >
          Dismiss
        </button>
      </div>
    </Alert>
  );
}

export default ProgramLinkStateStrip;
